import { useEffect, useState } from 'react';
import { FORM_MESSAGES, LABELS, UTILITY_CSS_CLASSES } from '../global/constants';
import { makeBEM, fadeOutThenExecute, fadeOutFormMsg, getDefaultFormMsg, gracefulFinally } from '../global/utils';
import { createNote, apiResultSuccess, editNote, deleteNote } from '../apihelper';
import FormButton from '../atoms/FormButton';
import FormStatus from '../atoms/FormStatus';
import { ConfirmDialog } from '../components/Dialogs';

function NoteForm({ moduleName='note-form', 
                    boardID=-1,
                    note,
                    discardForm,
                    deleteSuccess,
                    controller
                }) {

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [formData, setFormData] = useState({ body: '' });
	const [errors, setErrors] = useState({});
	const [formStatus, setFormStatus] = useState( FORM_MESSAGES.noteFormInstructions );
    const [editMode, setEditMode] = useState(false);
    const [fadeOutTimer, setFadeOutTimer] = useState(false);
    const [ctrl, setCtrl] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const formWrapperClass = `form ${moduleName}`;
    const inputsWrapperClass = `${makeBEM('form', 'inputs-wrapper')} ${makeBEM(moduleName, 'inputs-wrapper')}`;
    
    const showDeleteBtn = editMode;
    const btnsDisabled = isSubmitting || isDeleting;

    const bodyErrorClass = errors.body ? UTILITY_CSS_CLASSES.hasErrors : '';
    const bodyTextareaClass = `textarea ${makeBEM(moduleName, 'body-textarea')} ${bodyErrorClass}`

    useEffect(() => {
        if ( note ) {
            setFormData(note);
            setEditMode(true);
        }
    }, [note])

    useEffect(() => {
        if ( controller ) {
            setCtrl(controller)
        }
    }, [controller])


    // Change event handler for textarea
    // Updates formData with new value
    const handleChange = (e) => {
        const { name, value } = e.target;
        const updatedFormData = { ...formData, [name]: value };
        setFormData(updatedFormData);
    }

    // Validates form, checking for body
    const validateForm = () => {
        const newErrors = {};

        if ( !formData.body ) {
            newErrors.body = FORM_MESSAGES.bodyRequired;
        }

        return newErrors;
    }

    // Displays form status, in the following priority:
    // Errors | Message | Instructrions
    const getFormStatus = () => {
        if ( errors.body ) {
            return errors.body;
        }

        if ( formStatus ) {
            return formStatus;
        }

        return getDefaultFormMsg(editMode, FORM_MESSAGES.noteEdit, FORM_MESSAGES.noteFormInstructions);
    }

    // Submits form, calls API to create a new Note
    // New Note result gets appended to controller
    // Then fades out the form
    const handleSubmitNew = (postData) => {
        setIsSubmitting(true);
        setFormStatus( FORM_MESSAGES.newNoteSubmit );

        try {
            createNote(
                (results) => {
                    if ( apiResultSuccess(results) ) {
                        setFormStatus(FORM_MESSAGES.createdNewNote);
                        
                        if ( results.note ) {
                            if ( ctrl ) {
                                ctrl.addNew(results.note);
                            }
                        }

                        setFadeOutTimer(
                            fadeOutThenExecute(discardForm)
                        );
                    } else {
                        setFormStatus(FORM_MESSAGES.failedSubmit );
                    }
                },
                postData
            )
        } catch (error) {
            setFormStatus(FORM_MESSAGES.failedSubmit );
        } finally {
            gracefulFinally(setIsSubmitting);
        }
    }

    // Submits form, calls API to save edited Note
    // Note response is sent to the controller
    // Then fades out the form message
    const handleSubmitEdit = (postData) => {
        setIsSubmitting(true);
        setFormStatus( FORM_MESSAGES.editNoteSubmit );

        // TODO; This needs to be changed on the backend so postData id is accepted
        postData = { ...postData, note_id: postData.id  }

        try {
            editNote(
                (result) => {
                    if ( apiResultSuccess(result) ) {
                        setFormStatus( FORM_MESSAGES.savedNote );

                        if ( result.note ) {
                            if ( ctrl ) {
                                ctrl.editNote(result.note);
                            }
                            fadeOutFormMsg(setFormStatus, '');
                        } else {
                            setFormStatus( result.message )
                        }
                    }
                }, 
                postData
            )
        } catch(error) {
            setFormStatus( FORM_MESSAGES.failedSubmit );
        } finally {
            gracefulFinally(setIsSubmitting);
        }
    }

    // Helper to handle submit 
    // Calls the correct submit fn checking edit mode
    const handleSubmit = (e) => {
        e.preventDefault();
        const validationErrors = validateForm();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
        } else {
            setErrors({});

            const postData = { ...formData, board_id: boardID };

            if ( !editMode ) {
                handleSubmitNew(postData);
            } else {
                handleSubmitEdit(postData);
            }
            
        }
    }

    // Submits form, calls API to delete Note
    // Upon response, form status is updated
    // deleteSuccess is called upon fade out    
    const handleDelete = () => {
        discardConfirmDialog();
        const postData = { note_id: formData.id };
        setIsDeleting(true);

        try {
            deleteNote(
                (result) => {
                    if ( apiResultSuccess(result) ) {
                        setFormStatus(FORM_MESSAGES.deletedNote );
                        if ( deleteSuccess ) {
                            setFadeOutTimer(
                                fadeOutThenExecute(() => {
                                    deleteSuccess(postData.note_id);
                                })
                            );
                        }
                    } else {
                        setFormStatus(result.message );
                    }
                },
                postData
            )
        } catch {
            setFormStatus(FORM_MESSAGES.failedSubmit );
        } finally {
            gracefulFinally(setIsDeleting);
        }
    }

    const promptDeleteConfirm = () => {
        setShowConfirmDialog(true);
    }

    const discardConfirmDialog = () => {
        setShowConfirmDialog(false);
    }

    // Resets form data
    const resetForm = () => {
        setFormData({ title: '', body: '' });
        setErrors({});
    }

    // Handler for discarding a form
    // Calls discardForm 
    // Clears fadeOutTimer
    const handleDiscard = () => {
        resetForm();
        if ( discardForm ) {
            discardForm();
        }

        if ( fadeOutTimer ) {
            clearTimeout(fadeOutTimer);
        }
    }

    return (
        <div className={ formWrapperClass }>
            <form onSubmit={ handleSubmit }>
                
                <div className={ inputsWrapperClass }>
                    <FormStatus 
                        errors={errors} 
                        formStatus= { getFormStatus() }
                        editMode={editMode} 
                        customClass={ makeBEM(moduleName, 'form-status') } 
                    /> 
                    <textarea
                        id="body"
                        name="body"
                        type="text"
                        className={bodyTextareaClass}
                        onChange={ handleChange }
                        autoComplete="off"
                        required
                        placeholder={ LABELS.noteBodyPlaceholder }
                        value={ formData.body }
                        autoFocus
                    />

                    <div className={ `${makeBEM('form', 'btns-wrapper')} ${makeBEM(moduleName, 'btns-wrapper')}` }>
                        <FormButton
                            customClass={ makeBEM(moduleName, 'submit-btn') }
                            type="submit"
                            onClick={handleSubmit}
                            disabled={btnsDisabled}
                            isSubmitting={isSubmitting}
                        />

                        <FormButton
                            customClass={ makeBEM(moduleName, 'discard-btn') }
                            onClick={handleDiscard}
                            disabled={btnsDisabled}
                            type="button"
                            icon="close"
                        />

                        {
                            showDeleteBtn &&
                            <FormButton
                                customClass={ makeBEM(moduleName, 'delete-btn') }
                                onClick={promptDeleteConfirm}
                                disabled={btnsDisabled}
                                isSubmitting={isDeleting}
                                type="button"
                                icon="delete"
                            />
                        }
                    </div>
                </div>
            </form>
            {
                showConfirmDialog &&
                <ConfirmDialog 
                    show={showConfirmDialog}
                    message={LABELS.confirmDelete}
                    confirmAction={handleDelete}
                    discardAction={discardConfirmDialog}
                />
            }
        </div>
    )
}

export default NoteForm;